import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { useGetUserComments } from '../services/appService'; // Assuming hook exists

// Helper to format date/time
const formatDate = (dateInput) => {
    if (!dateInput) return 'N/A';
    try {
        const date = new Date(dateInput);
        if (isNaN(date.getTime())) return 'Invalid Date';
        return date.toLocaleString();
    } catch (e) {
        return 'Invalid Date';
    }
}

function UserCommentsModal({ open, onClose, userId, appId }) {
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const getUserCommentsHook = useGetUserComments();

    useEffect(() => {
        if (open && userId && appId) {
            setLoading(true);
            setError('');
            setComments([]); // Clear previous comments
            getUserCommentsHook(appId, userId)
                .then(data => {
                    setComments(data || []);
                })
                .catch(err => {
                    console.error("Error fetching user comments:", err);
                    setError(err.message || 'Failed to load comments.');
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [open, userId, appId, getUserCommentsHook]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
            <DialogTitle>Comments by User ({userId})</DialogTitle>
            <DialogContent dividers>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress />
                    </Box>
                )}
                {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
                {!loading && !error && (
                    <List dense>
                        {comments.length === 0 ? (
                            <ListItem>
                                <ListItemText primary="No comments found for this user." />
                            </ListItem>
                        ) : (
                            comments.map((comment) => (
                                <ListItem key={comment.id} divider alignItems="flex-start">
                                    <ListItemText
                                        primary={<Typography variant="body1">{comment.text}</Typography>}
                                        secondary={
                                            <>
                                                <Typography
                                                    sx={{ display: 'inline' }}
                                                    component="span"
                                                    variant="body2"
                                                    color="text.primary"
                                                >
                                                    {`Type: ${comment.type} | `}
                                                    {comment.related_title ? `Related: ${comment.related_title.substring(0, 50)}${comment.related_title.length > 50 ? '...' : ''} (${comment.related_id}) | ` : ''}
                                                </Typography>
                                                {formatDate(comment.created_at)}
                                            </>
                                        }
                                    />
                                </ListItem>
                            ))
                        )}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

UserCommentsModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    userId: PropTypes.string,
    appId: PropTypes.string.isRequired,
};

export default UserCommentsModal; 